/**
 * PaymentHeader — Reads payment data from Redux and displays metadata.
 */
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { useAppSelector } from 'src/store/hooks';
import { CopyableText } from 'src/components/common/CopyableText';
import { formatTimestamp } from 'src/utils/formatters';

const stateToIndicatorType = (state: string) => {
  if (state === 'SUCCESS') return 'success' as const;
  if (state === 'FAILED') return 'error' as const;
  return 'in-progress' as const;
};

export const PaymentHeader = () => {
  const payment = useAppSelector(state => state.payment.data);
  if (!payment) return null;

  const meta = payment.paymentMetaData;

  return (
    <Container
      header={
        <Header variant="h2">
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <CopyableText value={meta.paymentId} />
            <StatusIndicator type={stateToIndicatorType(meta.beaconPaymentState)}>
              {meta.beaconPaymentState}
            </StatusIndicator>
          </SpaceBetween>
        </Header>
      }
    >
      <ColumnLayout columns={5} variant="text-grid">
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Current Status</Box>
          <Box>{meta.beaconPaymentStatus}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Payment Type</Box>
          <Box><Badge color="blue">{meta.paymentType}</Badge></Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Bank</Box>
          <Box><Badge>{meta.bank}</Badge></Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Created</Box>
          <Box>{formatTimestamp(meta.createdAt)}</Box>
        </SpaceBetween>
        <SpaceBetween size="xxs">
          <Box variant="awsui-key-label">Last Updated</Box>
          <Box>{formatTimestamp(meta.updatedAt)}</Box>
        </SpaceBetween>
      </ColumnLayout>
    </Container>
  );
};
